/**
 * AgentDrawer — slide-in detail panel for a single agent.
 *
 * Renders from the right side as a fixed overlay (z-index 150).
 * Does NOT import useAgents — callers pass action callbacks as props.
 *
 * Props:
 *   agent     — agent object from useAgents, or null (renders nothing when null)
 *   onClose   — () => void
 *   onPause   — (reason: string) => Promise
 *   onResume  — () => Promise
 *   onKill    — (reason: string) => Promise
 */
import { useEffect, useRef, useState, useCallback } from "react";
import {
  X,
  Zap,
  Pause,
  Play,
  Skull,
  Clock,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Format an ISO timestamp as a relative human string, e.g. "3m ago", "2h ago".
 */
function timeAgo(isoString) {
  if (!isoString) return "never";
  const diffMs = Date.now() - new Date(isoString).getTime();
  if (diffMs < 0) return "just now";
  const secs  = Math.floor(diffMs / 1000);
  if (secs < 60)              return `${secs}s ago`;
  const mins  = Math.floor(secs / 60);
  if (mins < 60)              return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24)             return `${hours}h ago`;
  const days  = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Format a nullable USD number as "$X.XX" or "—".
 */
function fmtUsd(val) {
  if (val == null) return "—";
  return `$${Number(val).toFixed(2)}`;
}

/**
 * Format a fraction as a percentage string, e.g. 0.064 → "6.4%".
 */
function fmtPct(val) {
  if (val == null) return "—";
  return `${(val * 100).toFixed(1)}%`;
}

// ── Status chip ───────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  active:   { bg: "rgba(23,128,61,0.12)",  color: "var(--color-green)",    dot: "var(--color-green)"    },
  degraded: { bg: "rgba(224,122,78,0.12)", color: "#C45E28",               dot: "#E07A4E"               },
  paused:   { bg: "rgba(148,163,184,0.12)", color: "var(--color-slate-400)", dot: "var(--color-slate-400)" },
  killed:   { bg: "rgba(140,31,18,0.12)",  color: "var(--color-critical)", dot: "var(--color-critical)" },
};

function StatusChip({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.paused;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "2px 8px",
        borderRadius: 4,
        background: s.bg,
        color: s.color,
        fontSize: 11,
        fontFamily: "var(--font-mono)",
        border: `1px solid ${s.dot}33`,
        lineHeight: 1.4,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: s.dot,
          flexShrink: 0,
          display: "inline-block",
        }}
      />
      {status || "unknown"}
    </span>
  );
}

// ── Sparkline (7-bar SVG) ─────────────────────────────────────────────────────

function Sparkline({ data }) {
  if (!data || data.length === 0) return null;
  const W = 60;
  const H = 20;
  const max = Math.max(...data, 1);
  const barW = Math.floor((W - (data.length - 1) * 2) / data.length);

  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      aria-label="7-day sparkline"
      style={{ display: "inline-block", verticalAlign: "middle" }}
    >
      {data.map((val, i) => {
        const barH = Math.max(2, Math.round((val / max) * H));
        const x    = i * (barW + 2);
        const y    = H - barH;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barW}
            height={barH}
            rx={1}
            fill="var(--color-green)"
            opacity={i === data.length - 1 ? 1 : 0.55}
          />
        );
      })}
    </svg>
  );
}

// ── Section label ─────────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <p
      style={{
        margin: "0 0 8px",
        fontSize: 10,
        fontFamily: "var(--font-sans)",
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: "var(--color-slate-500)",
      }}
    >
      {children}
    </p>
  );
}

// ── Meta row ──────────────────────────────────────────────────────────────────

function MetaRow({ label, value, mono = false }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "5px 0",
        borderBottom: "1px solid var(--mc-border)",
        gap: 8,
      }}
    >
      <span
        style={{
          fontSize: 12,
          color: "var(--color-slate-500)",
          fontFamily: "var(--font-sans)",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 12,
          color: "var(--mc-text)",
          fontFamily: mono ? "var(--font-mono)" : "var(--font-sans)",
          textAlign: "right",
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AgentDrawer({ agent, onClose, onPause, onResume, onKill }) {
  // Pause inline form state
  const [pauseOpen, setPauseOpen]     = useState(false);
  const [pauseReason, setPauseReason] = useState("");
  const [pauseBusy, setPauseBusy]     = useState(false);
  const [pauseError, setPauseError]   = useState(null);

  // Kill confirm state
  const [killOpen, setKillOpen]       = useState(false);
  const [killInput, setKillInput]     = useState("");
  const [killBusy, setKillBusy]       = useState(false);
  const [killError, setKillError]     = useState(null);

  // Resume busy
  const [resumeBusy, setResumeBusy]   = useState(false);

  const pauseInputRef = useRef(null);
  const killInputRef  = useRef(null);

  // Reset inline form states when drawer opens a different agent
  useEffect(() => {
    setPauseOpen(false);
    setPauseReason("");
    setPauseError(null);
    setKillOpen(false);
    setKillInput("");
    setKillError(null);
  }, [agent?.agent_id]);

  // Close on Escape
  useEffect(() => {
    if (!agent) return;
    function handleKey(e) {
      if (e.key === "Escape") {
        if (killOpen)  { setKillOpen(false);  return; }
        if (pauseOpen) { setPauseOpen(false); return; }
        onClose();
      }
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [agent, onClose, killOpen, pauseOpen]);

  // Focus pause input when it opens
  useEffect(() => {
    if (pauseOpen && pauseInputRef.current) pauseInputRef.current.focus();
  }, [pauseOpen]);

  // Focus kill input when it opens
  useEffect(() => {
    if (killOpen && killInputRef.current) killInputRef.current.focus();
  }, [killOpen]);

  // ── Action handlers ────────────────────────────────────────────────────────

  const handlePauseSubmit = useCallback(async () => {
    if (!pauseReason.trim()) { setPauseError("Reason required."); return; }
    setPauseBusy(true);
    setPauseError(null);
    try {
      await onPause(pauseReason.trim());
      setPauseOpen(false);
      setPauseReason("");
    } catch (err) {
      setPauseError(err?.response?.data?.error || err.message || "Failed.");
    } finally {
      setPauseBusy(false);
    }
  }, [onPause, pauseReason]);

  const handleResume = useCallback(async () => {
    setResumeBusy(true);
    try {
      await onResume();
    } finally {
      setResumeBusy(false);
    }
  }, [onResume]);

  const handleKillSubmit = useCallback(async () => {
    if (killInput.trim() !== "KILL") { setKillError('Type exactly "KILL" to confirm.'); return; }
    setKillBusy(true);
    setKillError(null);
    try {
      await onKill("operator-confirmed kill via Mission Control");
      setKillOpen(false);
      setKillInput("");
      onClose();
    } catch (err) {
      setKillError(err?.response?.data?.error || err.message || "Failed.");
    } finally {
      setKillBusy(false);
    }
  }, [onKill, killInput, onClose]);

  // ── Guard ──────────────────────────────────────────────────────────────────

  if (!agent) return null;

  const isPlatform  = agent.kind === "platform";
  const isLegal     = agent.kind === "legal";
  const isActive    = agent.status === "active" || agent.status_computed === "active";
  const isPaused    = agent.status === "paused";
  const isKilled    = agent.status === "killed";

  const displayStatus = agent.status_computed || agent.status || "unknown";
  const lastActiveAt  = agent.last_run_at || agent.last_invoked_at;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 149,
        }}
      />

      {/* Panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Agent detail: ${agent.agent_id}`}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 400,
          zIndex: 150,
          backgroundColor: "var(--mc-bg)",
          borderLeft: "1px solid var(--mc-border)",
          display: "flex",
          flexDirection: "column",
          transform: "translateX(0)",
          transition: "transform 220ms cubic-bezier(0.16, 1, 0.3, 1)",
          boxShadow: "-8px 0 32px rgba(0,0,0,0.35)",
          overflowY: "auto",
        }}
        className="mc-agent-drawer"
      >

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div
          style={{
            height: 44,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 16px",
            borderBottom: "1px solid var(--mc-border)",
            background: "var(--mc-surface)",
            gap: 8,
          }}
        >
          <button
            onClick={onClose}
            aria-label="Close drawer"
            style={{
              background: "none",
              border: "none",
              color: "var(--color-slate-400)",
              cursor: "pointer",
              padding: 4,
              display: "flex",
              alignItems: "center",
              borderRadius: 4,
              flexShrink: 0,
            }}
          >
            <X size={16} />
          </button>

          <span
            className="mc-mono"
            style={{
              flex: 1,
              fontSize: 13,
              color: "var(--mc-text)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {agent.agent_id}
          </span>

          <StatusChip status={displayStatus} />
        </div>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 16px" }}>

          {/* §1  Core metadata */}
          <div style={{ marginBottom: 24 }}>
            <MetaRow label="Pod"    value={agent.pod  || "—"} />
            <MetaRow label="Kind"   value={agent.kind || "—"} />
            <MetaRow label="Class"  value={agent.agent_class || "—"} mono />

            <MetaRow
              label="Cost 24h"
              value={fmtUsd(agent.cost_24h_usd)}
              mono
            />

            {isPlatform && (
              <MetaRow
                label="Daily budget"
                value={agent.daily_budget_usd != null ? `${fmtUsd(agent.daily_budget_usd)}/day` : "—"}
                mono
              />
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "5px 0",
                borderBottom: "1px solid var(--mc-border)",
                gap: 8,
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 12,
                  color: "var(--color-slate-500)",
                  fontFamily: "var(--font-sans)",
                }}
              >
                <Clock size={11} /> Last active
              </span>
              <span
                className="mc-mono"
                style={{ fontSize: 12, color: "var(--mc-text)" }}
              >
                {timeAgo(lastActiveAt)}
              </span>
            </div>

            {agent.paused_reason && (
              <div
                style={{
                  marginTop: 8,
                  padding: "6px 10px",
                  borderRadius: 4,
                  background: "rgba(224,122,78,0.10)",
                  border: "1px solid rgba(224,122,78,0.25)",
                  fontSize: 12,
                  color: "#C45E28",
                  fontFamily: "var(--font-sans)",
                  display: "flex",
                  gap: 6,
                  alignItems: "flex-start",
                }}
              >
                <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                <span><strong>Paused:</strong> {agent.paused_reason}</span>
              </div>
            )}

            {agent.notes && (
              <div
                style={{
                  marginTop: 8,
                  padding: "6px 10px",
                  borderRadius: 4,
                  background: "var(--mc-surface)",
                  border: "1px solid var(--mc-border)",
                  fontSize: 12,
                  color: "var(--color-slate-400)",
                  fontFamily: "var(--font-sans)",
                }}
              >
                {agent.notes}
              </div>
            )}
          </div>

          {/* §2  Platform-specific: sparkline */}
          {isPlatform && agent.sparkline_7d && agent.sparkline_7d.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <SectionLabel>Recent 7 days</SectionLabel>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 0",
                  borderBottom: "1px solid var(--mc-border)",
                }}
              >
                <TrendingUp size={13} style={{ color: "var(--color-slate-500)", flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: "var(--color-slate-500)", fontFamily: "var(--font-sans)" }}>
                  Activity
                </span>
                <Sparkline data={agent.sparkline_7d} />
                <span className="mc-mono" style={{ fontSize: 11, color: "var(--color-slate-500)", marginLeft: "auto" }}>
                  {agent.sparkline_7d.reduce((a, b) => a + b, 0)} runs
                </span>
              </div>
            </div>
          )}

          {/* §3  Legal-specific: invocation stats */}
          {isLegal && (
            <div style={{ marginBottom: 24 }}>
              <SectionLabel>Legal agent stats</SectionLabel>

              {agent.total_invocations != null && (
                <MetaRow label="Total invocations" value={agent.total_invocations} mono />
              )}
              {agent.total_escalations != null && (
                <MetaRow label="Total escalations" value={agent.total_escalations} mono />
              )}
              {agent.escalation_rate != null && (
                <MetaRow label="Escalation rate" value={fmtPct(agent.escalation_rate)} mono />
              )}
              {agent.last_disposition && (
                <MetaRow label="Last disposition" value={agent.last_disposition} />
              )}
              {agent.pinned_version && (
                <MetaRow label="Pinned version" value={agent.pinned_version} mono />
              )}
            </div>
          )}

          {/* §4  Recent actions */}
          <div style={{ marginBottom: 24 }}>
            <SectionLabel>Recent actions (last 24h)</SectionLabel>
            <div
              style={{
                background: "var(--mc-surface)",
                borderRadius: 6,
                border: "1px solid var(--mc-border)",
                overflow: "hidden",
              }}
            >
              {agent.latest_action ? (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto auto",
                    gap: 8,
                    alignItems: "center",
                    padding: "8px 12px",
                    fontSize: 12,
                  }}
                >
                  <span
                    className="mc-mono"
                    style={{ color: "var(--mc-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {agent.latest_action.action_type}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      fontFamily: "var(--font-sans)",
                      color: agent.latest_action.outcome === "success"
                        ? "var(--color-green)"
                        : agent.latest_action.outcome === "failure"
                          ? "var(--color-critical)"
                          : "var(--color-slate-400)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {agent.latest_action.outcome || "—"}
                  </span>
                  <span
                    className="mc-mono"
                    style={{ fontSize: 10, color: "var(--color-slate-500)", whiteSpace: "nowrap" }}
                  >
                    {timeAgo(agent.latest_action.created_at)}
                  </span>
                </div>
              ) : (
                <div
                  style={{
                    padding: "12px",
                    fontSize: 12,
                    color: "var(--color-slate-500)",
                    fontFamily: "var(--font-sans)",
                    textAlign: "center",
                  }}
                >
                  No recent actions recorded.
                </div>
              )}
            </div>
          </div>

          {/* §5  Pause inline form */}
          {pauseOpen && (
            <div
              style={{
                marginBottom: 16,
                padding: "12px",
                background: "var(--mc-surface)",
                border: "1px solid var(--mc-border)",
                borderRadius: 6,
              }}
            >
              <p style={{ margin: "0 0 8px", fontSize: 12, color: "var(--mc-text)", fontFamily: "var(--font-sans)" }}>
                Reason for pausing:
              </p>
              <input
                ref={pauseInputRef}
                type="text"
                value={pauseReason}
                onChange={(e) => setPauseReason(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handlePauseSubmit(); }}
                placeholder="e.g. rate-limit holiday"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  background: "var(--mc-bg)",
                  border: "1px solid var(--mc-border)",
                  borderRadius: 4,
                  padding: "6px 10px",
                  fontSize: 12,
                  fontFamily: "var(--font-mono)",
                  color: "var(--mc-text)",
                  outline: "none",
                  marginBottom: 8,
                }}
              />
              {pauseError && (
                <p style={{ margin: "0 0 6px", fontSize: 11, color: "var(--color-critical)", fontFamily: "var(--font-sans)" }}>
                  {pauseError}
                </p>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={handlePauseSubmit}
                  disabled={pauseBusy}
                  style={{
                    flex: 1,
                    padding: "6px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    borderRadius: 4,
                    border: "none",
                    background: "#E07A4E",
                    color: "#fff",
                    cursor: pauseBusy ? "not-allowed" : "pointer",
                    opacity: pauseBusy ? 0.6 : 1,
                    fontFamily: "var(--font-sans)",
                  }}
                >
                  {pauseBusy ? "Pausing…" : "Confirm Pause"}
                </button>
                <button
                  onClick={() => { setPauseOpen(false); setPauseReason(""); setPauseError(null); }}
                  style={{
                    padding: "6px 12px",
                    fontSize: 12,
                    borderRadius: 4,
                    border: "1px solid var(--mc-border)",
                    background: "none",
                    color: "var(--color-slate-400)",
                    cursor: "pointer",
                    fontFamily: "var(--font-sans)",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* §6  Kill confirm form */}
          {killOpen && (
            <div
              style={{
                marginBottom: 16,
                padding: "12px",
                background: "rgba(140,31,18,0.08)",
                border: "1px solid rgba(140,31,18,0.30)",
                borderRadius: 6,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 8,
                }}
              >
                <AlertTriangle size={13} style={{ color: "var(--color-critical)", flexShrink: 0 }} />
                <p style={{ margin: 0, fontSize: 12, color: "var(--color-critical)", fontFamily: "var(--font-sans)", fontWeight: 600 }}>
                  This action is irreversible. Type KILL to confirm.
                </p>
              </div>
              <input
                ref={killInputRef}
                type="text"
                value={killInput}
                onChange={(e) => setKillInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleKillSubmit(); }}
                placeholder="KILL"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  background: "var(--mc-bg)",
                  border: "1px solid rgba(140,31,18,0.40)",
                  borderRadius: 4,
                  padding: "6px 10px",
                  fontSize: 13,
                  fontFamily: "var(--font-mono)",
                  color: "var(--color-critical)",
                  outline: "none",
                  letterSpacing: "0.08em",
                  marginBottom: 8,
                }}
              />
              {killError && (
                <p style={{ margin: "0 0 6px", fontSize: 11, color: "var(--color-critical)", fontFamily: "var(--font-sans)" }}>
                  {killError}
                </p>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={handleKillSubmit}
                  disabled={killBusy || killInput.trim() !== "KILL"}
                  style={{
                    flex: 1,
                    padding: "6px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    borderRadius: 4,
                    border: "none",
                    background: "var(--color-critical)",
                    color: "#fff",
                    cursor: (killBusy || killInput.trim() !== "KILL") ? "not-allowed" : "pointer",
                    opacity: (killBusy || killInput.trim() !== "KILL") ? 0.5 : 1,
                    fontFamily: "var(--font-sans)",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    justifyContent: "center",
                  }}
                >
                  <Skull size={13} />
                  {killBusy ? "Killing…" : "Kill Agent"}
                </button>
                <button
                  onClick={() => { setKillOpen(false); setKillInput(""); setKillError(null); }}
                  style={{
                    padding: "6px 12px",
                    fontSize: 12,
                    borderRadius: 4,
                    border: "1px solid var(--mc-border)",
                    background: "none",
                    color: "var(--color-slate-400)",
                    cursor: "pointer",
                    fontFamily: "var(--font-sans)",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

        </div>

        {/* ── Footer actions ──────────────────────────────────────────────── */}
        {!pauseOpen && !killOpen && (
          <div
            style={{
              flexShrink: 0,
              padding: "12px 16px",
              borderTop: "1px solid var(--mc-border)",
              background: "var(--mc-surface)",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {/* Pause + Resume row */}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => setPauseOpen(true)}
                disabled={isPaused || isKilled}
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  padding: "7px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  borderRadius: 4,
                  border: "1px solid var(--mc-border)",
                  background: "none",
                  color: (isPaused || isKilled) ? "var(--color-slate-600)" : "var(--mc-text)",
                  cursor: (isPaused || isKilled) ? "not-allowed" : "pointer",
                  opacity: (isPaused || isKilled) ? 0.5 : 1,
                  fontFamily: "var(--font-sans)",
                }}
              >
                <Pause size={13} />
                Pause Agent
              </button>

              <button
                onClick={handleResume}
                disabled={isActive || isKilled || resumeBusy}
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  padding: "7px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  borderRadius: 4,
                  border: "1px solid var(--mc-border)",
                  background: "none",
                  color: (isActive || isKilled || resumeBusy) ? "var(--color-slate-600)" : "var(--color-green)",
                  cursor: (isActive || isKilled || resumeBusy) ? "not-allowed" : "pointer",
                  opacity: (isActive || isKilled || resumeBusy) ? 0.5 : 1,
                  fontFamily: "var(--font-sans)",
                }}
              >
                <Play size={13} />
                {resumeBusy ? "Resuming…" : "Resume"}
              </button>
            </div>

            {/* Kill — platform agents only */}
            {isPlatform && !isKilled && (
              <button
                onClick={() => setKillOpen(true)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  padding: "7px 12px",
                  fontSize: 12,
                  fontWeight: 700,
                  borderRadius: 4,
                  border: "1px solid rgba(140,31,18,0.45)",
                  background: "rgba(140,31,18,0.08)",
                  color: "var(--color-critical)",
                  cursor: "pointer",
                  fontFamily: "var(--font-sans)",
                  letterSpacing: "0.04em",
                }}
              >
                <Skull size={13} />
                Kill Agent
                <AlertTriangle size={11} style={{ marginLeft: 2 }} />
              </button>
            )}
          </div>
        )}
      </aside>
    </>
  );
}
